"use client";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Shield,
  Laptop,
  ArrowRight,
  ChevronRight,
  BookOpen,
  Briefcase,
  Brain,
  Code,
  Network,
  ChevronLeft,
  Target,
  Compass,
  Award,
  Heart,
  Lightbulb,
  Globe,
  Zap,
} from "lucide-react";
// import logoImg from "/logo.png";
import Navbar from "../Header/Navbar";
import Footer from "../Header/Footer";
import { useNavigate } from "react-router-dom";
import ApplyButton from "../common/ApplyButton";

// Carousel images - replace these with your actual images
const carouselImages = [
  // "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
  // "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
  // "https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
  // "https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1486&q=80",
  "images/about/header/oriantion-day1.jpg",
  "images/about/header/DSC_0267.JPG",
  "images/about/header/hp-gaming-garag.jpg",
  "images/about/header/anusal-dinner.jpg",
];

const UniversityPage = () => {
  const [activeTab, setActiveTab] = useState("academics");
  const [activeProgram, setActiveProgram] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Vision Mission section animation
  const [visionRef, visionInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Handle automatic carousel sliding
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Pause autoplay when user interacts with carousel
  const pauseAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
    // Resume after 10 seconds of inactivity
    const timeout = setTimeout(() => setIsAutoPlaying(true), 10000);
    return () => clearTimeout(timeout);
  }, []);

  // Navigation functions for carousel
  const nextSlide = useCallback(() => {
    pauseAutoPlay();
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  }, [pauseAutoPlay]);

  const prevSlide = useCallback(() => {
    pauseAutoPlay();
    setCurrentSlide(
      (prev) => (prev - 1 + carouselImages.length) % carouselImages.length
    );
  }, [pauseAutoPlay]);

  const goToSlide = useCallback(
    (index) => {
      pauseAutoPlay();
      setCurrentSlide(index);
    },
    [pauseAutoPlay]
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const stats = [
    // {
    //   number: "15,000+",
    //   label: "Students",
    //   icon: <Users className="w-8 h-8" />,
    // },
    // {
    //   number: "200+",
    //   label: "Academic Programs",
    //   icon: <BookOpen className="w-8 h-8" />,
    // },
    // {
    //   number: "98%",
    //   label: "Graduate Employment Rate",
    //   icon: <Briefcase className="w-8 h-8" />,
    // },
    // {
    //   number: "100+",
    //   label: "Research Centers",
    //   icon: <BarChart2 className="w-8 h-8" />,
    // },
  ];

  const academicPrograms = [
    {
      title: "Computer Science",
      description:
        "Develop cutting-edge software and solve complex computational problems.",
      icon: <Laptop className="w-8 h-8" />,
      image:
        "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    },

    {
      title: "Cybersecurity",
      description:
        "Protect digital assets and combat cyber threats in an interconnected world.",
      icon: <Shield className="w-8 h-8" />,
      image:
        "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    },
    {
      title: "Artificial Intelligence",
      description:
        "Create intelligent systems that can learn, adapt, and solve complex problems.",
      icon: <Brain className="w-8 h-8" />,
      image:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=1632&q=80",
    },
    {
      title: "Software Engineering",
      description:
        "Design, develop, and maintain complex software systems for various industries.",
      icon: <Code className="w-8 h-8" />,
      image:
        "https://images.unsplash.com/photo-1605379399642-870262d3d051?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fA%3D%3D&auto=format&fit=crop&w=1506&q=80",
    },
  ];

  const campusFeatures = [
    {
      title: "Innovation Hub",
      description:
        "A collaborative space where ideas come to life, featuring state-of-the-art prototyping labs and startup incubators.",
      icon: <Laptop size={32} />,
      image:
        "https://media.istockphoto.com/id/2167777446/photo/communication-technology-smart-connection-and-people-network-technology.webp?a=1&b=1&s=612x612&w=0&k=20&c=hxZw3jCQLifMOZ1LaXuhGz6eOylfMehzY30uCXSlA_8=",
    },
    {
      title: "Digital Library",
      description:
        "Access a wide range of resources and leading research papers from around the world.",
      icon: <BookOpen size={32} />,
      image:
        "https://media.istockphoto.com/id/1363779378/photo/online-education-concept.webp?a=1&b=1&s=612x612&w=0&k=20&c=RZWPe0KeBmocHM2dBx24btRYISs-CA-Moxv5dJeJ6eo=",
    },
    {
      title: "Tech Park",
      description:
        "A bridge between academia and industry, hosting tech giants and promising startups alike.",
      icon: <Briefcase size={32} />,
      image:
        "https://media.istockphoto.com/id/1169517546/photo/smart-transportation.webp?a=1&b=1&s=612x612&w=0&k=20&c=cdBVXqfjIV0y5f8AcU0BYgSQS5YFhLHiAfpiMgos8f8=",
    },
    {
      title: "Smart Classrooms",
      description:
        "Equipped with the latest educational technology to enhance learning experiences.",
      icon: <Network size={32} />,
      image: "images/about/infra2.png",
    },
  ];

  const admissionSteps = [
    {
      title: "Submit Application",
      description:
        "Submit your online application with academic transcripts, personal statement, and recommendation letters through our portal.",
    },
    {
      title: "Aptitude Assessment",
      description:
        "Take our aptitude test designed to evaluate your critical thinking and program-specific knowledge.",
    },
    {
      title: "Candidate Shortlisting",
      description:
        "Our admissions committee reviews applications and test results to create a shortlist of promising candidates.",
    },
    {
      title: "Interview",
      description:
        "Shortlisted candidates meet with faculty for a final interview to discuss their academic interests and career goals.",
    },
  ];

  // Vision, Mission, and Core Values data
  const coreValues = [
    {
      title: "Excellence",
      description:
        "Striving for the highest standards in education, research, and innovation.",
      icon: <Award className="w-10 h-10 text-yellow-500" />,
    },
    {
      title: "Integrity",
      description:
        "Upholding ethical principles and honesty in all academic and professional endeavors.",
      icon: <Shield className="w-10 h-10 text-blue-600" />,
    },
    {
      title: "Innovation",
      description:
        "Fostering creative thinking and pioneering approaches to solve complex problems.",
      icon: <Lightbulb className="w-10 h-10 text-amber-500" />,
    },
    {
      title: "Inclusivity",
      description:
        "Embracing diversity and creating an environment where all can thrive and contribute.",
      icon: <Heart className="w-10 h-10 text-red-500" />,
    },
    {
      title: "Global Perspective",
      description:
        "Cultivating a worldview that prepares students for international challenges and opportunities.",
      icon: <Globe className="w-10 h-10 text-green-600" />,
    },
    {
      title: "Impact",
      description:
        "Committing to research and education that creates positive change in society and industry.",
      icon: <Zap className="w-10 h-10 text-purple-600" />,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "academics":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto px-4 py-16"
          >
            <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">
              Academic Excellence
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-12">
              {academicPrograms.map((program, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2"
                >
                  <img
                    src={program.image || "/placeholder.svg"}
                    alt={program.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="text-blue-600 mr-4">{program.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {program.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">
                      {program.description}
                    </p>
                    {/* <button className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors duration-300">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button> */}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );

      case "campus":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto px-4 py-16"
          >
            <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">
              Campus Life
            </h2>
            <div className="grid md:grid-cols-2 gap-12">
              {campusFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2"
                >
                  <img
                    src={feature.image || "/placeholder.svg"}
                    alt={feature.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="text-blue-600 mr-4">{feature.icon}</div>
                      <h3 className="text-2xl font-semibold text-gray-800">
                        {feature.title}
                      </h3>
                    </div>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );
      case "admissions":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto px-4 py-16"
          >
            <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">
              Admissions Process
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>
              <div className="space-y-16">
                {admissionSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative flex items-center"
                  >
                    <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {index + 1}
                    </div>
                    <div
                      className={`w-5/12 ${
                        index % 2 === 0 ? "pr-8 text-right" : "pl-8 ml-auto"
                      }`}
                    >
                      <div className="bg-white p-6 rounded-lg shadow-lg">
                        <h3 className="text-xl font-semibold mb-2 text-gray-800">
                          {step.title}
                        </h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Carousel */}
      <Navbar />
      <div className="relative min-h-screen">
        {/* Carousel */}
        <div className="relative h-screen overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${carouselImages[currentSlide]})`,
              }}
            />
          </AnimatePresence>

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/50">
            <div className="max-w-7xl mx-auto px-4 h-full flex flex-col items-center justify-center text-center">
              <motion.img
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                src="./logo.png"
                alt="NASTP University Logo"
                className="w-24 h-24 mb-8 rounded-full"
              />
              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-5xl font-bold mb-4 text-white"
              >
                Innovate. Impact. Inspire.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-xl mb-8 text-white/90 max-w-2xl"
              >
                At NIIT University, we empower the next generation of tech
                visionaries through transformative education, advanced
                innovation, and research that drives the future.
              </motion.p>
            </div>
          </div>

          {/* Carousel Navigation */}
          <div className="absolute inset-x-0 bottom-10 flex justify-center items-center gap-4 z-10">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSlide === index ? "bg-white scale-125" : "bg-white/50"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Carousel Controls */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 z-10"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 z-10"
            aria-label="Next slide"
          >
            {/* <ArrowRight size={24} /> */}
            <ChevronRight size={24} />
          </button>
        </div>
      </div>

      {/* Vision, Mission & Core Values Section */}
      <div ref={visionRef} className="pt-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={visionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-gray-800">
              Vision & Mission
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Our guiding principles shape everything we do at our university,
              from curriculum development to campus culture.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 mb-20">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={visionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl shadow-lg relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative z-10">
                <div className="bg-blue-600 text-white p-4 rounded-full inline-flex items-center justify-center mb-6">
                  <Target className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">
                  Our Vision
                </h3>
                <p className="text-gray-700 mb-6">
                  To be a globally recognized institution that transforms
                  education through technology, fostering innovation and
                  producing graduates who lead positive change in society and
                  industry.
                </p>
                <div className="w-16 h-1 bg-blue-600"></div>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={visionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 rounded-xl shadow-lg relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-200 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative z-10">
                <div className="bg-emerald-600 text-white p-4 rounded-full inline-flex items-center justify-center mb-6">
                  <Compass className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-800">
                  Our Mission
                </h3>
                <p className="text-gray-700 mb-6">
                  To provide exceptional education that combines theoretical
                  knowledge with practical skills, conduct groundbreaking
                  research, and collaborate with industry partners to address
                  real-world challenges.
                </p>
                <div className="w-16 h-1 bg-emerald-600"></div>
              </div>
            </motion.div>
          </div>

          {/* Core Values */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={visionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <h3 className="text-3xl font-bold text-center mb-12 text-gray-800">
              Our Core Values
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreValues.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={visionInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-t-4 border-blue-600"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">{value.icon}</div>
                    <div>
                      <h4 className="text-xl font-semibold mb-2 text-gray-800">
                        {value.title}
                      </h4>
                      <p className="text-gray-600">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="pt-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-4">
                  <div className="text-blue-600">{stat.icon}</div>
                </div>
                <div className="text-3xl font-bold mb-1 text-gray-800">
                  {stat.number}
                </div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`sticky top-0 z-10 bg-white shadow-md transition-all duration-300 ${
          isScrolled ? "py-2" : "py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-center">
            {["academics", "campus", "admissions"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === tab
                    ? "text-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">{renderTabContent()}</AnimatePresence>

      {/* Call to Action */}
      {/* <div className="bg-gray-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto px-4 relative z-10 text-center"
        >
          <h2 className="text-4xl font-bold text-white mb-6">
            Shape the Future of Technology
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join NIIT and be part of the next generation of tech innovators and
            leaders. Your journey to excellence starts here.
          </p>
          <ApplyButton
            to="/login"
            className="bg-white text-gray-900 px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors duration-300 shadow-lg inline-flex items-center"
          >
            Apply Now <ArrowRight size={16} className="ml-2" />
          </ApplyButton>
        </motion.div>
      </div> */}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UniversityPage;
